<template>
  <RevTable :collection="combinedMarkets" :columns transparent-header>
    <template #body-market="{ item }">
      <Flag class="mr-8" :country-code="item.countryCode" size="m" />
      {{ i18n.country(item.countryCode) }}
    </template>
    <template #body-currentBackboxPrice="{ item }">
      <span v-if="!item.currentBackboxPrice">-</span>
      <span v-else>{{
        i18n.price(item.currentBackboxPrice.amount, {
          currency: item.currentBackboxPrice.currency as Currency,
        })
      }}</span>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Currency } from '@backmarket/http-api'
import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTable } from '@ds/components/Table'
import type { Column } from '@ds/components/Table/Table.constant'

import type { ListingV2, MarketListing } from '~/api/listings/types'

import translations from './PricingInsights.translations'

const props = defineProps<{
  listing: ListingV2
}>()

const i18n = useI18n()

const columns: Column[] = [
  {
    key: 'market',
    label: i18n(translations.market),
  },
  {
    key: 'currentBackboxPrice',
    label: i18n(translations.currentBackboxPrice),
    description: i18n(translations.currentBackboxPriceDescription),
  },
]

function transformMarketsWithCountryCode(
  markets: Partial<Record<MarketCountryCode, MarketListing>>,
) {
  return Object.entries(markets).map(([countryCode, marketListing]) => ({
    ...marketListing,
    countryCode: countryCode as MarketCountryCode,
  }))
}

const combinedMarkets = computed(() => [
  ...transformMarketsWithCountryCode(props.listing.markets.withBackbox),
  ...transformMarketsWithCountryCode(props.listing.markets.withoutBackbox),
  ...transformMarketsWithCountryCode(props.listing.markets.offline),
])
</script>
